import React, { Fragment } from 'react';
// Link import removed - using Button component with 'to' prop instead
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { ThemeToggle } from '../UI/ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';
import { clsx } from 'clsx';
import Button from '../UI/Button';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="sm"
        className="-m-2.5 p-2.5 lg:hidden"
        onClick={onMenuClick}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </Button>

      {/* Separator */}
      <div className="h-6 w-px bg-border lg:hidden" aria-hidden="true" />

      <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
        {/* Search */}
        <div className="relative flex flex-1">
          {/* You can add a search component here if needed */}
        </div>

        <div className="flex items-center gap-x-4 lg:gap-x-6">
          {/* Theme Toggle */}
          <ThemeToggle className="-m-2.5 p-2.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-all duration-200 active-scale" />

          {/* Notifications */}
          <Button
            variant="ghost"
            size="sm"
            className="-m-2.5 p-2.5 relative"
          >
            <span className="sr-only">View notifications</span>
            <BellIcon className="h-6 w-6" aria-hidden="true" />
            {/* Notification badge */}
            <span className="absolute -top-1 -right-1 h-2 w-2 bg-destructive rounded-full"></span>
          </Button>

          {/* Separator */}
          <div
            className="hidden lg:block lg:h-6 lg:w-px lg:bg-border"
            aria-hidden="true"
          />

          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <Menu.Button className="-m-1.5 flex items-center p-1.5 rounded-lg hover:bg-accent transition-all duration-200 active-scale">
              <span className="sr-only">Open user menu</span>
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md ring-2 ring-background">
                <span className="text-sm font-semibold text-primary-foreground">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="hidden lg:flex lg:items-center">
                <span
                  className="ml-3 text-sm font-semibold leading-6 text-foreground"
                  aria-hidden="true"
                >
                  {user?.name}
                </span>
                <span className="ml-1 text-xs text-muted-foreground">
                  {user?.role}
                </span>
              </span>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95 translate-y-1"
              enterTo="transform opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="transform opacity-100 scale-100 translate-y-0"
              leaveTo="transform opacity-0 scale-95 translate-y-1"
            >
              <Menu.Items className="absolute right-0 z-10 mt-3 w-56 origin-top-right rounded-xl bg-background border border-border shadow-lg ring-1 ring-black/5 focus:outline-none py-1 animate-scaleIn">
                <div className="px-1 py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <Button
                        to="/profile"
                        className={clsx(
                          active ? 'bg-accent text-accent-foreground' : 'text-foreground',
                          'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md hover:bg-accent hover:text-accent-foreground w-full justify-start'
                        )}
                        variant="ghost"
                      >
                        <UserCircleIcon className="mr-3 h-4 w-4" />
                        Your profile
                      </Button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Button
                        to="/settings"
                        className={clsx(
                          active ? 'bg-accent text-accent-foreground' : 'text-foreground',
                          'flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md hover:bg-accent hover:text-accent-foreground w-full justify-start'
                        )}
                        variant="ghost"
                      >
                        <Cog6ToothIcon className="mr-3 h-4 w-4" />
                        Settings
                      </Button>
                    )}
                  </Menu.Item>
                  <div className="my-1 h-px bg-border" />
                  <Menu.Item>
                    {({ active }) => (
                      <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className={clsx(
                          active ? 'bg-destructive/10 text-destructive' : 'text-destructive',
                          'flex w-full items-center px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md hover:bg-destructive/10 hover:text-destructive'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                        Sign out
                      </Button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
};

export default Header;